import { apiClient } from '../../services/apiClient';
import type {
  SeedancePresetActor,
  SeedancePresetActorsResponse,
  SeedancePresetActorFilters,
  CreateSeedancePresetActorRequest,
  UpdateSeedancePresetActorRequest,
  SeedancePresetImportPreview,
  ImportSeedancePresetActorRequest,
  BulkImportResponse,
  SeedancePresetImportLog,
} from './types';

const USER_PATH = '/seedance-preset-actors';
const ADMIN_PATH = '/admin/seedance-preset-actors';

// === エンドユーザー向け ===

export async function listSeedancePresetActors(
  filters?: SeedancePresetActorFilters
): Promise<SeedancePresetActorsResponse> {
  const params: Record<string, string> = {};
  if (filters?.gender) params.gender = filters.gender;
  if (filters?.age !== undefined && filters.age !== null) params.age = String(filters.age);
  if (filters?.active_only !== undefined) params.active_only = filters.active_only ? 'true' : 'false';
  const { data } = await apiClient.get<SeedancePresetActorsResponse>(USER_PATH, { params });
  return data;
}

// === Admin 向け ===

export async function listAdminSeedancePresetActors(): Promise<SeedancePresetActorsResponse> {
  const { data } = await apiClient.get<SeedancePresetActorsResponse>(ADMIN_PATH);
  return data;
}

function buildFormDataForCreate(request: CreateSeedancePresetActorRequest): FormData {
  const fd = new FormData();
  fd.append('byteplus_asset_id', request.byteplus_asset_id);
  fd.append('display_name', request.display_name);
  if (request.display_name_en) fd.append('display_name_en', request.display_name_en);
  if (request.gender) fd.append('gender', request.gender);
  if (request.min_age !== undefined) fd.append('min_age', String(request.min_age));
  if (request.max_age !== undefined) fd.append('max_age', String(request.max_age));
  if (request.ethnicity) fd.append('ethnicity', request.ethnicity);
  if (request.tags) fd.append('tags', JSON.stringify(request.tags));
  if (request.description) fd.append('description', request.description);
  if (request.visibility) fd.append('visibility', request.visibility);
  if (request.thumbnail) fd.append('thumbnail', request.thumbnail);
  return fd;
}

export async function createSeedancePresetActor(
  request: CreateSeedancePresetActorRequest
): Promise<{ actor: SeedancePresetActor }> {
  const fd = buildFormDataForCreate(request);
  const { data } = await apiClient.post<{ actor: SeedancePresetActor }>(ADMIN_PATH, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function updateSeedancePresetActor(
  actorId: string,
  request: UpdateSeedancePresetActorRequest
): Promise<{ actor: SeedancePresetActor }> {
  // thumbnail があれば multipart、なければ JSON
  if (request.thumbnail) {
    const fd = new FormData();
    if (request.display_name !== undefined) fd.append('display_name', request.display_name);
    if (request.display_name_en !== undefined) fd.append('display_name_en', request.display_name_en ?? '');
    if (request.gender !== undefined) fd.append('gender', request.gender ?? '');
    if (request.min_age !== undefined) fd.append('min_age', request.min_age === null ? '' : String(request.min_age));
    if (request.max_age !== undefined) fd.append('max_age', request.max_age === null ? '' : String(request.max_age));
    if (request.ethnicity !== undefined) fd.append('ethnicity', request.ethnicity ?? '');
    if (request.country !== undefined) fd.append('country', request.country ?? '');
    if (request.tags !== undefined) fd.append('tags', JSON.stringify(request.tags));
    if (request.description !== undefined) fd.append('description', request.description ?? '');
    if (request.visibility !== undefined) fd.append('visibility', request.visibility);
    if (request.is_active !== undefined) fd.append('is_active', request.is_active ? 'true' : 'false');
    fd.append('thumbnail', request.thumbnail);
    const { data } = await apiClient.patch<{ actor: SeedancePresetActor }>(
      `${ADMIN_PATH}/${actorId}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  }
  const { data } = await apiClient.patch<{ actor: SeedancePresetActor }>(
    `${ADMIN_PATH}/${actorId}`, request,
  );
  return data;
}

// asset_id から BytePlus のメタ情報を取得 (DB 保存なし)
export async function previewImportSeedancePresetActor(
  byteplusAssetId: string,
): Promise<{ preview: SeedancePresetImportPreview }> {
  const { data } = await apiClient.post<{ preview: SeedancePresetImportPreview }>(
    `${ADMIN_PATH}/import/preview`,
    { byteplus_asset_id: byteplusAssetId },
  );
  return data;
}

// asset_id から取り込んで DB 登録 (顔写真はサーバ側で DL → 保存)
export async function importSeedancePresetActor(
  request: ImportSeedancePresetActorRequest,
): Promise<{ actor: SeedancePresetActor }> {
  const { data } = await apiClient.post<{ actor: SeedancePresetActor }>(
    `${ADMIN_PATH}/import`,
    request,
  );
  return data;
}

// asset_id を複数まとめて取り込む (失敗はスキップして履歴に記録)
export async function bulkImportSeedancePresetActors(
  assetIds: string[],
  visibility?: 'private' | 'organization' | 'public',
): Promise<BulkImportResponse> {
  const { data } = await apiClient.post<BulkImportResponse>(`${ADMIN_PATH}/import/bulk`, {
    asset_ids: assetIds,
    visibility,
  });
  return data;
}

// 取り込み履歴を取得
export async function listSeedancePresetImportLogs(
  params?: { limit?: number; status?: 'imported' | 'skipped' | 'failed' },
): Promise<{ logs: SeedancePresetImportLog[] }> {
  const query: Record<string, string> = {};
  if (params?.limit) query.limit = String(params.limit);
  if (params?.status) query.status = params.status;
  const { data } = await apiClient.get<{ logs: SeedancePresetImportLog[] }>(
    `${ADMIN_PATH}/import/logs`,
    { params: query },
  );
  return data;
}

export async function deleteSeedancePresetActor(actorId: string): Promise<void> {
  await apiClient.delete(`${ADMIN_PATH}/${actorId}`);
}

export async function reorderSeedancePresetActors(
  orders: { id: string; display_order: number }[]
): Promise<void> {
  await apiClient.post(`${ADMIN_PATH}/reorder`, { orders });
}
